<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, getCurrentInstance, computed, watch, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from "@kyvg/vue3-notification"
import TranscoderService from '@/services/TranscoderService'
import { mediaPulseModel } from "../../components/Composable/mediaPulse"
import FormButton from '../../components/Atoms/FormButton.vue'
import IconBase from '../../components/icon/IconBase.vue'
import FormSwitch from '../Atoms/FormSwitch.vue'
import StreamSourceDropdown from '../ui/StreamSourceDropdown.vue'
import IconCodec from '../icon/IconCodec.vue'
import IconSpeaker from '../icon/IconSpeaker.vue'
import IconBitrate from '../icon/IconBitrate.vue'
import IconCheckmark from '../icon/IconCheckmark.vue'
import { track, EVENTS } from '@/lib/tracking'

const instance = getCurrentInstance()
const root = instance.proxy.$root
const store = useStore()
const { notify } = useNotification()

const props = defineProps({
  transcoder: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id')
    }
  },
  mediaPulse: {
    type: Object,
    default() { return {} }
  },
  streamAlive: {
    type: Boolean
  },
})

// Refs
const toggleProcessing = ref(false)

// Media Pulse
const { subscribeMediaPulse, unsubscribeMediaPulse, mediaPulse: destinationMediaPulse } = mediaPulseModel()


// Computed
const hasLiveSubscription = computed(() => store.getters['User/hasLiveSubscription'])

// Watch
watch(() => props.transcoder, async () => {
  subscribeMediaPulse(props.transcoder?.destination)
}, { immediate: true })

// Hooks
onUnmounted(() => {
  unsubscribeMediaPulse()
})
// Methods

const setDestination = async (item) => {
  try {
    const data = { destination: item ? item._id : item }
    await TranscoderService.updateTranscoder(
      props.transcoder._id,
      data
    )

    await store.dispatch('Transcoders/getTranscoder', props.transcoder._id)
    unsubscribeMediaPulse()
    if (props.transcoder.destination) subscribeMediaPulse(props.transcoder.destination._id)
    if (item) {
      track(EVENTS.TRANSCODER_DESTINATION_CONFIGURED, { transcoderId: props.transcoder._id })
    }
    notify({
      group: 'success',
      text: `Destination updated`,
    })

  } catch (err) {
    console.log(err)
    notify({ group: 'error', text: err.message || 'Destination update failed' })
  }
}

const destinationStreamToggle = async () => {
  if (props.transcoder.destination) {
    toggleProcessing.value = true
    try {
      await TranscoderService.updateTranscoder(props.transcoder._id, {
        destinationEnabled: !props.transcoder.destinationEnabled,
      })
      await store.dispatch("Transcoders/getTranscoders")
      await store.dispatch("Transcoders/getTranscoder", props.transcoder._id)
      unsubscribeMediaPulse()
      subscribeMediaPulse(props.transcoder.destination._id)
      notify({
        group: "success",
        text: `Stream ${props.transcoder.destination.name} ${props.transcoder.destinationEnabled ? "enabled" : "disabled"
          }`,
      })
    } catch (err) {
      notify({
        group: "error",
        title: "Couldn't toggle stream status",
        text: err.message,
      })

      if (err.message && err.message.indexOf("upgrade") > -1) {
        showModal("billing-prompt")
      }
    }
    toggleProcessing.value = false
  }
}

</script>
<template>
  <section class="max-w-[100vw] flex flex-col gap-4">
    <!-- Destination (primary action, top) -->
    <div class="bg-surface-2 rounded-xl p-4 md:p-5">
      <div class="flex items-center justify-between mb-0.5">
        <h3 class="text-base font-semibold text-white mb-0">Destination</h3>
        <FormButton class="!text-surface-7 hover:!text-error" type="link" size="sm"
          v-if="props.transcoder?.destination" @click="setDestination(null)">
          Remove
        </FormButton>
      </div>
      <p class="text-tiny text-surface-7 mb-3">Send the transcoded output to one of your streams.</p>

      <label class="block mb-1 text-sm text-surface-8">Link to</label>
      <div class="flex items-center gap-2">
        <stream-source-dropdown :disabled="!hasLiveSubscription?.enabled" @on-show="onSourceDropdownShow"
          @on-hide="onSourceDropdownHide" @on-source-selected="setDestination" :dropRight="false"
          :selectedStream="props.transcoder?.destination" :showPlaylist="false" source="transcoder"
          placeholder="Select destination" only-live-selector class="grow min-w-0" />

        <FormSwitch v-if="props.transcoder?.destination" class="shrink-0"
          :modelValue="props.transcoder.destinationEnabled" @change="destinationStreamToggle"
          :disabled="toggleProcessing">
        </FormSwitch>
      </div>
      <p v-if="!props.transcoder?.destination" class="text-tiny text-surface-6 mt-2 mb-0">
        Pick a stream to start sending the transcoded output to it.
      </p>
      <div v-else-if="destinationMediaPulse?.alive" class="text-success text-tiny flex items-center gap-1.5 mt-3">
        <IconBase>
          <IconCheckmark />
        </IconBase>
        <span>Connected</span>
      </div>
    </div>

    <!-- Stream comparison (only while live) -->
    <div v-if="props.streamAlive" class="bg-surface-2 rounded-xl p-4 md:p-5">
      <div class="bg-surface-1 rounded-lg p-3">
        <h4 class="text-tiny font-semibold uppercase tracking-wider text-surface-6 mb-2">Incoming stream</h4>
        <ul v-if="props.mediaPulse?.meta?.video" class="flex flex-col gap-2 text-tiny text-surface-9">
          <li class="flex gap-2 items-center">
            <IconBase class="text-purple shrink-0">
              <IconCodec />
            </IconBase>
            {{ mediaPulse.meta.video.codec }} &#x2022;
            {{ mediaPulse.meta.video.width }} x {{ mediaPulse.meta.video.height }} ({{ mediaPulse.meta.video.bitrate }}kbps)
          </li>
          <li v-if="props.mediaPulse?.meta?.audio" class="flex gap-2 items-center">
            <IconBase class="text-success shrink-0">
              <IconSpeaker />
            </IconBase>
            {{ mediaPulse.meta.audio.codec }} {{ mediaPulse.meta.audio.channels === 2 ? 'stereo' : '' }} ({{ mediaPulse.meta.audio.bitrate }}kbps)
          </li>
          <li class="flex gap-2 items-center">
            <IconBase class="text-pink shrink-0">
              <IconBitrate />
            </IconBase>
            {{ mediaPulse.meta.video.fps }} FPS
          </li>
        </ul>
        <p v-else class="text-tiny text-surface-6 mb-0">Waiting for stream data…</p>
      </div>

      <div class="bg-surface-1 rounded-lg p-3 mt-3">
        <h4 class="text-tiny font-semibold uppercase tracking-wider text-surface-6 mb-2">Transcoded</h4>
        <ul v-if="destinationMediaPulse?.meta?.video" class="flex flex-col gap-2 text-tiny text-surface-9">
          <li class="flex gap-2 items-center">
            <IconBase class="text-purple shrink-0">
              <IconCodec />
            </IconBase>
            {{ destinationMediaPulse.meta.video.codec }} &#x2022; {{ destinationMediaPulse.meta.video.width }}
            x {{ destinationMediaPulse.meta.video.height }} ({{ destinationMediaPulse.meta.video.bitrate }}kbps)
          </li>
          <li v-if="destinationMediaPulse.meta?.audio" class="flex gap-2 items-center">
            <IconBase class="text-success shrink-0">
              <IconSpeaker />
            </IconBase>
            {{ destinationMediaPulse.meta.audio.codec }} {{ destinationMediaPulse.meta.audio.channels === 2 ? 'stereo' : '' }} ({{ destinationMediaPulse.meta.audio.bitrate }}kbps)
          </li>
          <li class="flex gap-2 items-center">
            <IconBase class="text-pink shrink-0">
              <IconBitrate />
            </IconBase>
            {{ destinationMediaPulse.meta.video.fps }} FPS
          </li>
        </ul>
        <p v-else-if="!props.transcoder?.destination" class="text-tiny text-surface-6 mb-0">
          Add a destination to see the transcoded stats.
        </p>
        <p v-else class="text-tiny text-surface-6 mb-0">Offline</p>
      </div>
    </div>

    <!-- Offline placeholder so the column reads intentionally, not empty -->
    <div v-else class="bg-surface-2 rounded-xl p-5 text-center">
      <p class="text-tiny text-surface-6 mb-0 leading-snug">
        Input and output details appear here once the stream is live.
      </p>
    </div>
  </section>
</template>